import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { Dispatch, ReactNode, SetStateAction } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useTwoCollapsableCardsState } from '@/hooks/useTwoCollapsableCardsState';

import { EmptyStateErrorUnexpected } from '../EmptyState';

type LayoutControllerProps<DataType> = {
  isFirstCardCollapsed: boolean;
  isSecondCardCollapsed: boolean;
  isOpen: boolean;
  setData: Dispatch<SetStateAction<DataType | null>>;
  handleOpenSecondCard: (data: DataType) => void;
  handleFirstCardCollapse: () => void;
  handleSecondCardCollapse: () => void;
  handleAccordionOpen: () => void;
  data: DataType | null;
};

type TableWithDetailCardProps<DataType> = {
  isError: boolean;
  renderTable: (props: LayoutControllerProps<DataType>) => ReactNode;
  renderDetailCard: (
    props: LayoutControllerProps<DataType> & {
      // Defined `data` as non-nullable DataType since detail card cannot be
      // rendered without a selected row (data).
      data: DataType;
    }
  ) => ReactNode;
};

export function TableWithDetailCard<T>({
  isError,
  renderTable,
  renderDetailCard,
}: TableWithDetailCardProps<T>) {
  const { isMobile } = useBreakpoint();

  const {
    data,
    isFirstCardCollapsed,
    isSecondCardCollapsed,
    isOpen,
    setData,
    handleOpenSecondCard,
    handleFirstCardCollapse,
    handleSecondCardCollapse,
    handleAccordionOpen,
  } = useTwoCollapsableCardsState<T>();

  const renderProps = {
    data,
    isFirstCardCollapsed,
    isSecondCardCollapsed,
    isOpen,
    setData,
    handleOpenSecondCard,
    handleFirstCardCollapse,
    handleSecondCardCollapse,
    handleAccordionOpen,
  };

  if (isError) {
    return <EmptyStateErrorUnexpected />;
  }

  return (
    <div
      className={clsx('main flex min-h-full w-full gap-size-24', {
        'flex-wrap': isMobile,
      })}
    >
      <div
        className={clsx(
          'column-1 flex h-auto flex-col items-start gap-size-8',
          !isFirstCardCollapsed && 'flex-grow'
        )}
      >
        <Card
          isAccordion={isMobile}
          lineSeparator={isOpen}
          isCollapsable={data !== null && !isMobile}
          isCollapsed={isFirstCardCollapsed && !isMobile}
          onCollapse={handleFirstCardCollapse}
          isOpen={isOpen}
          onOpen={handleAccordionOpen}
          classes={{
            header: clsx(!isMobile && 'py-3'),
          }}
        >
          {renderTable(renderProps)}
        </Card>
      </div>

      {data && (
        <div
          className={clsx(
            'column-2 flex flex-col items-start gap-size-8',
            isFirstCardCollapsed && 'flex-grow',
            isMobile && !isSecondCardCollapsed && 'flex-grow',
            {
              'max-w-[500px]': !isFirstCardCollapsed && !isMobile,
              'w-full': !isSecondCardCollapsed,
            }
          )}
        >
          <div
            className={clsx('sticky top-[10px] flex w-full', {
              'h-full': isSecondCardCollapsed,
            })}
          >
            {renderDetailCard({
              ...renderProps,
              data,
            })}
          </div>
        </div>
      )}
    </div>
  );
}
